import { format } from "date-fns";
import { Tables } from "@/integrations/supabase/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Calendar, MapPin, Briefcase, DollarSign } from "lucide-react";

type JobPosting = Tables<"job_postings">;

interface JobDetailsDialogProps {
  job: JobPosting;
  isOpen: boolean;
  onClose: () => void;
}

export const JobDetailsDialog = ({ job, isOpen, onClose }: JobDetailsDialogProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{job.title}</span>
            <Badge variant={job.is_active ? "default" : "secondary"}>
              {job.is_active ? "Active" : "Inactive"}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{job.department}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{job.location}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm font-medium">Employment Type: </span>
              <span className="text-sm capitalize">{job.employment_type}</span>
            </div>
            {job.salary_range && (
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{job.salary_range}</span>
              </div>
            )}
          </div>

          <Separator />

          {/* Description */}
          <div>
            <h3 className="font-semibold mb-2">Job Description</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {job.description}
            </p>
          </div>

          {/* Requirements */}
          {job.requirements && job.requirements.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">Requirements</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                {job.requirements.map((requirement, index) => (
                  <li key={index}>{requirement}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Benefits */}
          {job.benefits && job.benefits.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">Benefits</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                {job.benefits.map((benefit, index) => (
                  <li key={index}>{benefit}</li>
                ))}
              </ul>
            </div>
          )}

          <Separator />

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Posted: </span>
              <span className="text-muted-foreground">
                {job.posted_date ? format(new Date(job.posted_date), "PPP") : "Not set"}
              </span>
            </div>
            {job.application_deadline && (
              <div>
                <span className="font-medium">Deadline: </span>
                <span className="text-muted-foreground">
                  {format(new Date(job.application_deadline), "PPP")}
                </span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Created: </span>
              <span className="text-muted-foreground">
                {format(new Date(job.created_at!), "PPP")}
              </span>
            </div>
            <div>
              <span className="font-medium">Updated: </span>
              <span className="text-muted-foreground">
                {format(new Date(job.updated_at!), "PPP")}
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};